import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE = { super_admin: "Super admin", major_admin: "Major admin", admin: "Admin", venue_owner: "Business owner", staff: "Staff", customer: "Customer" };

/**
 * Shared frame for every dashboard: sidebar navigation on desktop, scrollable tabs on
 * phones, and a clearly labelled Logout button in both places.
 * items: [{ key, label, Icon, badge?, group? }]
 */
export default function DashboardShell({ title, subtitle, items, active, onSelect, actions, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };
  const initials = (user?.name || user?.email || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  let lastGroup = null;

  return (
    <div className="min-h-[calc(100vh-68px)] paper lg:grid lg:grid-cols-[264px_1fr]">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col sticky top-[68px] h-[calc(100vh-68px)] bg-white border-r border-[color:var(--line)]">
        <div className="p-5 border-b border-[color:var(--line)] flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-[color:var(--maroon)] text-[color:var(--gold-2)] flex items-center justify-center font-num text-lg shrink-0">{initials}</span>
          <div className="min-w-0">
            <div className="font-semibold text-[color:var(--ink)] truncate">{user?.name || "Your account"}</div>
            <div className="text-[13px] text-neutral-500">{ROLE[user?.role] || user?.role}</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3" aria-label="Dashboard sections">
          {items.map((it) => {
            const showGroup = it.group && it.group !== lastGroup;
            lastGroup = it.group || lastGroup;
            const on = active === it.key;
            return (
              <div key={it.key}>
                {showGroup && <div className="px-3 pt-4 pb-1.5 text-[12px] font-semibold text-neutral-400">{it.group}</div>}
                <button onClick={() => onSelect(it.key)} className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] text-left transition-colors press ${on ? "text-[color:var(--maroon)] font-semibold" : "text-[color:var(--ink-2)] hover:bg-[color:var(--cream)]"}`} data-testid={`dash-nav-${it.key}`} aria-current={on ? "page" : undefined}>
                  {on && <motion.span layoutId="dash-active" className="absolute inset-0 rounded-xl bg-[color:var(--ivory)]" transition={{ type: "spring", damping: 30, stiffness: 380 }}/>}
                  <it.Icon className="relative w-[18px] h-[18px] shrink-0"/>
                  <span className="relative flex-1 truncate">{it.label}</span>
                  {it.badge ? <span className="relative text-[12px] min-w-[22px] h-[22px] px-1.5 rounded-full bg-[color:var(--maroon)] text-white flex items-center justify-center font-semibold">{it.badge}</span> : null}
                </button>
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[color:var(--line)] space-y-1.5">
          <Link to="/" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] text-[color:var(--ink-2)] hover:bg-[color:var(--cream)]"><ExternalLink className="w-[18px] h-[18px]"/> View website</Link>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-semibold text-rose-700 hover:bg-rose-50 press" data-testid="dash-logout"><LogOut className="w-[18px] h-[18px]"/> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <div className="min-w-0">
        <div className="lg:hidden sticky top-[68px] z-30 bg-white/95 backdrop-blur border-b border-[color:var(--line)]">
          <div className="flex gap-1 overflow-x-auto no-scrollbar px-3 py-2">
            {items.map((it) => (
              <button key={it.key} onClick={() => onSelect(it.key)} className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[14px] press ${active === it.key ? "bg-[color:var(--maroon)] text-white font-semibold" : "text-[color:var(--ink-2)] bg-[color:var(--cream)]"}`}>
                <it.Icon className="w-4 h-4"/> {it.label}{it.badge ? <span className="ml-0.5 text-[11px] font-bold">({it.badge})</span> : null}
              </button>
            ))}
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-10 py-7 lg:py-9 max-w-[1500px]">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
            <div className="min-w-0">
              <h1 className="font-display text-[34px] md:text-[44px] leading-[1.05] text-[color:var(--maroon)]">{title}</h1>
              {subtitle && <p className="text-[15px] text-[color:var(--ink-2)] mt-1.5">{subtitle}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {actions}
              <button onClick={doLogout} className="lg:hidden inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-rose-200 text-rose-700 font-semibold text-[15px] bg-white press" data-testid="dash-logout-mobile"><LogOut className="w-4 h-4"/> Logout</button>
            </div>
          </div>
          <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/** Long numbers (e.g. ₹12,45,999) get a smaller size so the full amount is always visible. */
function valueSize(v) {
  const n = String(v ?? "").length;
  if (n <= 6) return "text-[30px] md:text-[34px]";
  if (n <= 9) return "text-[26px] md:text-[30px]";
  if (n <= 12) return "text-[22px] md:text-[26px]";
  return "text-[19px] md:text-[22px]";
}

/** KPI tile used across dashboards. */
export function Tile({ icon: I, label, value, hint, tone = "default", children }) {
  const toneCls = tone === "alert" ? "border-rose-200 bg-rose-50/50" : tone === "good" ? "border-emerald-200" : "border-[color:var(--line)]";
  return (
    <div className={`rounded-[18px] bg-white border ${toneCls} p-4 md:p-5 min-w-0`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[14px] text-[color:var(--ink-2)] font-semibold truncate">{label}</span>
        {I && <I className="w-4 h-4 text-[color:var(--gold)] shrink-0"/>}
      </div>
      <div className={`font-num ${valueSize(value)} leading-[1.05] text-[color:var(--ink)] mt-3 tabular-nums`} style={{ overflowWrap: "anywhere" }} title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}>{value ?? "–"}</div>
      {hint && <div className="text-[13px] text-neutral-500 mt-1.5 truncate">{hint}</div>}
      {children}
    </div>
  );
}

/** Section card with a title row. */
export function Panel({ title, action, children, className = "" }) {
  return (
    <section className={`rounded-[20px] bg-white border border-[color:var(--line)] p-5 md:p-6 min-w-0 ${className}`}>
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {title && <h2 className="font-display text-[22px] md:text-[24px] leading-tight text-[color:var(--maroon)]">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
